import React, { useState } from 'react'
import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux'
import { withRouter } from 'react-router-dom'
import { FieldState } from 'final-form'
import { Field as FinalFormField } from 'react-final-form'

import { getRandom as getRandomString } from 'utils/string'

import { RootState } from 'state/types'

import {
  getQuoteRequestValue,
  getQuoteErrorValue,
} from 'state/quotes/selectors'

import { QuotePaths } from 'state/quotes/types'
import {
  setCurrentQuoteRequestEntries,
  clearCurrentQuoteError,
} from 'state/quotes/actions'

import { validate } from 'lib/validation'

import Checkbox from './Checkbox'
import Input from './Input'
import Radio from './Radio'
import RangeSlider from './RangeSlider'
import Select from './Select'
import TextArea from './TextArea'

import { Type as InputType } from './Input/types'

import {
  FinalFormInput,
  OnChange,
  Type,
  Props,
  Value,
} from './types'

let clearDebounceTimeout: Function

const InputWrapperContainer: React.FC<Props> = ({
  additionalQuoteRequestFieldsToUpdate,
  defaultValue,
  label,
  mutateValue = (value: Value): Value => value,
  name,
  quoteRequest = 'current',
  quoteRequestField,
  type = Type.text,
  asyncValidate,
  asyncValidateDebounceMs = 500,
  value: valueProp,
  onChange: onChangeProp,
  ...props
}): React.ReactElement | null => {
  const isRedux = (quoteRequest && quoteRequestField && true) || false

  const [
    touched,
    setTouched,
  ] = useState<boolean|undefined>(undefined)

  // Convert boolean to 'false' and 'true' (to prevent false from being ignored in logical operations)
  const stringValueProp = (valueProp === true || valueProp === false) ? valueProp.toString() : valueProp

  const [
    valueState,
    setValueState,
  ] = useState(!isRedux && stringValueProp)

  const dispatch = useDispatch()
  const [
    fieldError,
    valueStore,
  ] = useSelector((state: RootState) => {
    const quoteRequestValue = quoteRequestField && getQuoteRequestValue(state, quoteRequest, quoteRequestField)
    const stringQuoteRequestValue = (quoteRequestValue === true || quoteRequestValue === false) ? quoteRequestValue.toString() : quoteRequestValue

    return [
      (quoteRequestField && getQuoteErrorValue(state, QuotePaths.RateCallOne, quoteRequestField)) || undefined,
      (isRedux && stringQuoteRequestValue) || undefined,
    ]
  }, shallowEqual)

  const setValue = (value: Value): void => {
    if (isRedux) {
      if (typeof additionalQuoteRequestFieldsToUpdate === 'function') {
        additionalQuoteRequestFieldsToUpdate = additionalQuoteRequestFieldsToUpdate(value)
      }

      const kvps =
        (Array.isArray(additionalQuoteRequestFieldsToUpdate) &&
          additionalQuoteRequestFieldsToUpdate.reduce((acc, key) => {
            key && (acc[key] = value)

            return acc
          }, {})) ||
        (typeof additionalQuoteRequestFieldsToUpdate === 'object' && additionalQuoteRequestFieldsToUpdate) ||
        {}
      if (quoteRequestField) {
        kvps[quoteRequestField] = mutateValue(value)
      }

      dispatch(setCurrentQuoteRequestEntries(kvps))
    }
    else {
      setValueState(mutateValue(value))
    }
  }

  const value = stringValueProp || valueStore || (!isRedux && valueState)
  const stringHtmlValue = (value || defaultValue || '').toString()

  // Convert 'false' and 'true' to boolean
  const htmlValue = (stringHtmlValue === 'true' || stringHtmlValue === 'false') ? JSON.parse(stringHtmlValue) : stringHtmlValue

  if (!value && htmlValue) {
    setValue(htmlValue)
  }

  const onChange: OnChange = (value, ...options): void => {
    onChangeProp && onChangeProp(value, ...options)

    setValue(value)
  }

  const labelHuman = (label || name || '').replace(/([A-Z])/g, ' $1').toLowerCase()
  const errorPrefix = ((type === Type.radio || type === Type.select) && `A valid selection${(labelHuman && ' for') || ''}`) || ''
  const errorSuffix = `${labelHuman || (!(type === Type.radio || type === Type.select) && 'This input') || ''} is required.`
  const errorText = props.errorText || `${errorPrefix}${errorSuffix}`

  const Component =
    (InputType[type] && Input) ||
    (type === Type.address && Input) ||
    (type === Type.checkbox && Checkbox) ||
    (type === Type.date && Input) ||
    (type === Type.radio && Radio) ||
    (type === Type.rangeSlider && RangeSlider) ||
    (type === Type.select && Select) ||
    (type === Type.textArea && TextArea) ||
    undefined

  if (!Component) return null // eslint-disable-line no-null/no-null

  const componentProps = {
    ...props,
    alwaysShowError: !!fieldError,
    createdByCorrectParent: true,
    defaultValue: defaultValue as Value,
    label,
    name,
    quoteRequest,
    quoteRequestField,
    type,
    value: htmlValue as Value,
  }

  return (
    (quoteRequestField &&
      React.createElement(
        FinalFormField,
        {
          name: name as string,
          type,
          initialValue: htmlValue,
          validate: async (value: unknown, allFormValues: unknown, formField: FieldState<unknown> | undefined): Promise<string | undefined> => {
            if (fieldError && formField && !formField.dirty) {
              return (Array.isArray(fieldError) ? fieldError[0] : fieldError) || errorText
            }

            const { isValid: isSyncValid } = validate(props.rules, value as Value)

            if (!isSyncValid) {
              return errorText
            }

            let isAsyncValid = true
            if (asyncValidate) {
              isAsyncValid = await new Promise(resolve => {
                if (clearDebounceTimeout) {
                  clearDebounceTimeout()
                }

                const timeout = setTimeout(() => {
                  const asyncResponse = asyncValidate(value)

                  resolve(asyncResponse)
                }, asyncValidateDebounceMs)

                clearDebounceTimeout = (): void => {
                  clearTimeout(timeout)
                  resolve(false)
                }
              })
            }

            if (isAsyncValid === false) {
              return errorText
            }

            // Passed validation

            quoteRequestField && dispatch(clearCurrentQuoteError('rateCallOne', quoteRequestField))

            return undefined // return undefined when validation passes
          },
        },
        ({
          input: finalFormInput,
          meta: finalFormMeta,
        }: {
          input: FinalFormInput,
          meta: FieldState<unknown>,
        }) => {
          return React.createElement(Component, {
            ...finalFormInput,
            ...finalFormMeta,
            ...componentProps,
            touched: touched !== undefined ? touched : finalFormMeta.touched,
            onBlur: () => {
              setTouched(true)
            },
            onChange: (event: React.ChangeEvent<HTMLInputElement>, ...options: any) => {
              finalFormInput.onChange(event)

              const value = (event.target.type === 'checkbox') ? event.target.checked : event.target.value
              onChange && onChange(value, ...options)
            },
          })
        },
      )) ||
    React.createElement(Component, {
      ...componentProps,
      valid: true,
      onChange: (event: React.ChangeEvent<HTMLInputElement>, ...options: any) =>
        onChange && onChange(event.target.value, ...options),
    })
  )
}

export * from './types'

export default withRouter(InputWrapperContainer)

/**
 * Generate a random name for an input, typicaly used to block autofill from firing, such as when using a 3rd-party autosuggest
 * Make sure to prefix it with an alpha character, to prevent Final Form from ever ever throwing "Cannot set a numeric property on an object" error (https://github.com/final-form/react-final-form/issues/103)
 */
export const getRandomInputName = () => `ff_${getRandomString(7)}`
